import { Component, computed, Input, input } from "@angular/core";
import { CalendarData } from "../../modules/home/calendar/CalendarData";
import { S25ItemModule } from "../../modules/s25-item/s25.item.module";
import { S25RegisterButtonModule } from "../../modules/s25-register-button/s25.register.button.module";
import { Event } from "../../pojo/Event";
import { AccessLevels } from "../../pojo/Fls";
import { FlsService } from "../../services/fls.service";
import { s25Resource } from "../../util/s25.resource";
import { CalendarReservationDateRange } from "./calendar.reservation.date.range.component";
import { CalendarSubject } from "./calendar.subject.component";

@Component({
    selector: "s25-ng-calendar-reservation",
    standalone: true,
    imports: [CalendarReservationDateRange, CalendarSubject, S25ItemModule, S25RegisterButtonModule],
    host: {
        class: "ngCalendarDayEventItem CalendarDayEventItem",
        "[class.cancelled-reservation]": "reservation().itemState == Cancelled",
        "[class.blackout]": "isBlackoutEvent()",
        "[class.lastBlackout]": "lastBlackout()",
        "[class.lastAllDayEvent]": "lastAllDayEvent()",
        "[class.allDayEvent]": "reservation().all_day",
        "[class.allDayBlackout]": "isBlackoutEvent() && reservation().all_day",
        "[class.lastAllDayBlackout]": "lastAllDayBlackout()",
        "[class.hasHeader]": "showAllDayHeader()",
        "[class.noHeader]": "reservation().all_day && !showAllDayHeader()",
    },
    styleUrls: ["./calendar.reservation.component.scss"],
    template: `
        @if (showAllDayHeader()) {
            <div class="allDayHeader">All Day</div>
        }
        @if (reservation().itemState == Cancelled) {
            <div class="icon icon-cancelled ngInlineBlock" aria-label="Cancelled Occurrence"></div>
        }
        @if (!reservation().all_day && (reservation().rsrv_start_dt || reservation().rsrv_end_dt)) {
            <s25-ng-calendar-reservation-date-range
                [reservation]="reservation()"
                [class.ngInlineBlock]="reservation().itemState == Cancelled"
            ></s25-ng-calendar-reservation-date-range>
        }
        @if (hasAnyEventProperties()) {
            <div class="event-item-wrapper">
                @if (isBlackoutEvent()) {
                    <div class="s25-item-holder">
                        <span class="bold s25-item-name blackout-event-name">{{ eventName() }}</span>
                    </div>
                } @else {
                    <s25-item-event
                        class="bold eventItem ngCalendarEvent ngItemContent"
                        [itemId]="reservation().event_id"
                        [itemName]="eventName()"
                        [contextMenuOnBody]="true"
                    ></s25-item-event>
                }
                @if (canRegister()) {
                    <s25-ng-register-button
                        [itemId]="reservation().event_id"
                        [modelBean]="{
                            itemId: reservation().event_id,
                            title: '',
                        }"
                    ></s25-ng-register-button>
                }
            </div>
        }
        @if (reservation().profile_name) {
            <span class="ngReservationName reservation_name {{ Reservation.classes(reservation()) }}">
                {{ reservation().profile_name }}
            </span>
        }
        @for (subject of reservation().subject; track subject.itemId; let last = $last) {
            <s25-ng-calendar-subject
                class="ngCalendarSubject"
                [subject]="subject"
                [reservation]="reservation()"
                [isLast]="last"
            />
        }
        @if (reservation().reg_url) {
            <div class="icon-tickets">
                <a target="_blank" rel="noopener" [href]="reservation().reg_url.val">
                    {{ reservation().reg_url.txt }}
                </a>
            </div>
        }
    `,
})
export class CalendarReservation {
    readonly reservation = input.required<CalendarData.Reservation>();
    readonly lastBlackout = input.required<boolean>();
    readonly lastAllDayEvent = input.required<boolean>();
    readonly lastAllDayBlackout = input.required<boolean>();
    readonly showAllDayHeader = input.required<boolean>();

    private readonly eventCommerceFLS = s25Resource({
        // returning 'undefined' means the request will not fire
        params: () => (this.reservation().has_registration ? true : undefined),
        loader: () => FlsService.property("EVENT_COMMERCE"),
    });

    readonly isBlackoutEvent = computed(() => !this.reservation().event_id);
    readonly canRegister = computed(() => (this.eventCommerceFLS.value() ?? AccessLevels.None) !== AccessLevels.None);
    readonly eventName = computed(() => this.reservation().event_name || this.reservation().itemName);
    readonly hasAnyEventProperties = computed(() => this.reservation().event_id || this.eventName());

    protected readonly Reservation = CalendarData.Reservation;
    protected readonly Cancelled = Event.State.Ids.Cancelled;
}
